import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, FileText, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Shared documentation modal — reused by:
 *   1. Native /clock clock-out flow (blocking: must complete before clock_out posts)
 *   2. Retroactive Add Note flow on the Timesheets table (CSV/EVV/legacy rows)
 *
 * The 7 service options + min-note-length come from
 * GET /api/timesheets/service-options so the FE never hardcodes the
 * enum. One documentation standard, one validation rule, regardless of
 * how the visit arrived.
 *
 * Props:
 *   open               — controlled open state
 *   onOpenChange       — controlled-state setter
 *   title              — e.g. "Document this visit" or "Add Note"
 *   description        — short context line above the form
 *   submitLabel        — defaults to "Save documentation"
 *   initialService     — pre-fill (used on correction path); null/undefined = empty
 *   initialNote        — pre-fill (used on correction path); null/undefined = empty
 *   onSubmit({service_provided, service_note}) → returns a Promise. Caller decides
 *                                                  what API to call. Modal closes
 *                                                  on resolved promise and stays open
 *                                                  on rejected promise (with toast).
 */
export default function DocumentationModal({
  open,
  onOpenChange,
  title = "Document this visit",
  description = "Select the service you provided and write a short summary.",
  submitLabel = "Save documentation",
  initialService = "",
  initialNote = "",
  onSubmit,
}) {
  const [opts, setOpts] = useState({ options: [], service_note_min_chars: 20 });
  const [service, setService] = useState(initialService || "");
  const [note, setNote] = useState(initialNote || "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setService(initialService || "");
    setNote(initialNote || "");
    api.get("/timesheets/service-options")
      .then((r) => setOpts(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [open, initialService, initialNote]);

  const trimmedLen = useMemo(
    () => (note || "").replace(/\s/g, "").length,
    [note],
  );
  const noteOk = trimmedLen >= opts.service_note_min_chars;
  const serviceOk = !!service && opts.options.includes(service);
  const formOk = noteOk && serviceOk;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!formOk || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({ service_provided: service, service_note: note.trim() });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="documentation-modal"
        onEscapeKeyDown={(e) => submitting && e.preventDefault()}
        onPointerDownOutside={(e) => submitting && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary" style={{ fontFamily: "Outfit" }}>
            <FileText size={18} /> {title}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="accp-label block mb-1.5" htmlFor="doc-service">
              Service provided <span className="text-destructive">*</span>
            </label>
            <select
              id="doc-service"
              data-testid="doc-service-select"
              value={service}
              onChange={(e) => setService(e.target.value)}
              disabled={submitting || opts.options.length === 0}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">— Select a service —</option>
              {opts.options.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="accp-label flex items-center justify-between mb-1.5" htmlFor="doc-note">
              <span>Service note <span className="text-destructive">*</span></span>
              <span className={`text-[11px] font-normal ${noteOk ? "text-muted-foreground" : "text-[#8A5A00]"}`}>
                {trimmedLen} / {opts.service_note_min_chars}+ non-whitespace chars
              </span>
            </label>
            <textarea
              id="doc-note"
              data-testid="doc-note-textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              rows={5}
              placeholder="Describe what you did during the visit, the supports provided, and the outcome…"
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
            {!noteOk && trimmedLen > 0 && (
              <p className="text-[11px] text-[#8A5A00] mt-1 flex items-center gap-1">
                <AlertTriangle size={11} /> Note must contain at least {opts.service_note_min_chars} non-whitespace characters.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="accp-btn-ghost"
              data-testid="doc-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formOk || submitting}
              className="accp-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="doc-submit-btn"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {submitLabel}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
